<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Copy01Icon, Link05Icon } from '@hugeicons/core-free-icons';

import HugeIcon from 'next/icon/HugeIcon.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  showCopy: { type: Boolean, default: false },
  showCopyLink: { type: Boolean, default: false },
  /** `trailing` = inline-end (opposite default avatar on incoming); `leading` = inline-start (opposite outgoing avatar) */
  side: {
    type: String,
    required: true,
    validator: v => ['trailing', 'leading'].includes(v),
  },
});

const { t } = useI18n();
const { getPlainText } = useMessageFormatter();
const currentAccountId = useMapGetter('getCurrentAccountId');

const plainText = computed(() =>
  getPlainText(props.message.content ?? '', false)
);

const visible = computed(() => props.showCopy || props.showCopyLink);

async function handleCopy() {
  await copyTextToClipboard(plainText.value);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
}

async function handleCopyLink() {
  const fullConversationURL =
    window.chatwootConfig.hostURL +
    frontendURL(
      conversationUrl({
        id: props.message.conversation_id,
        accountId: currentAccountId.value,
      })
    );
  await copyTextToClipboard(
    `${fullConversationURL}?messageId=${props.message.id}`
  );
  useAlert(t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
}
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <template v-if="visible">
    <div
      class="pointer-events-none absolute -top-4 z-10 flex items-start opacity-0 transition-opacity duration-150 ease-out group-hover/message-row:pointer-events-auto group-hover/message-row:opacity-100"
      :class="side === 'trailing' ? 'end-2 md:end-3' : 'start-2 md:start-3'"
      role="toolbar"
      :aria-label="t('CONVERSATION.CONTEXT_MENU.QUICK_ACTIONS')"
    >
      <div
        class="flex items-center gap-px rounded-md border border-n-weak bg-n-solid-2 p-px shadow-sm dark:border-n-strong dark:bg-n-solid-1"
        @click.stop
      >
        <button
          v-if="showCopy"
          v-tooltip.top="t('CONVERSATION.CONTEXT_MENU.COPY')"
          type="button"
          class="flex size-8 min-h-8 min-w-8 items-center justify-center rounded-sm p-0 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.COPY')"
          @click="handleCopy"
        >
          <HugeIcon
            :icon="Copy01Icon"
            :size="20"
            class="pointer-events-none shrink-0"
          />
        </button>
        <button
          v-if="showCopyLink"
          v-tooltip.top="t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK')"
          type="button"
          class="flex size-8 min-h-8 min-w-8 items-center justify-center rounded-sm p-0 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK')"
          @click="handleCopyLink"
        >
          <HugeIcon
            :icon="Link05Icon"
            :size="20"
            class="pointer-events-none shrink-0"
          />
        </button>
      </div>
    </div>
  </template>
</template>
